import { css } from 'styled-components';

/**
 * Design tokens ported verbatim from the Remix reference's `styles.css`
 * (`:root` and `.dark`). Values are kept in oklch exactly as authored there so
 * both apps render identical colours in both themes.
 *
 * Naming: every token is prefixed `--hd-` so it cannot collide with the app's
 * pre-existing globals (`--primary`, `--secondary`, `--white`, `--black`, …),
 * which mean different things and are used by hundreds of components.
 *
 * Use these — never a raw hex — in any new styled-component and the surface
 * becomes theme-aware with no further work.
 *
 *   page/app background .... var(--hd-background)
 *   panels, cards, bars .... var(--hd-card)
 *   primary text ........... var(--hd-foreground)
 *   secondary text ......... var(--hd-muted-foreground)
 *   hairlines, dividers .... var(--hd-border)
 *   subtle fills, hovers ... var(--hd-muted)
 */

/** Light palette. Declared on `:root`, so it is also the fallback. */
export const lightTokens = css`
  --hd-radius: 0.875rem;

  --hd-background: oklch(1 0 0);
  --hd-foreground: oklch(0.18 0.02 260);

  --hd-card: oklch(1 0 0);
  --hd-card-foreground: oklch(0.18 0.02 260);
  --hd-popover: oklch(1 0 0);
  --hd-popover-foreground: oklch(0.18 0.02 260);

  --hd-primary: oklch(0.55 0.2 258); /* #2563EB */
  --hd-primary-foreground: oklch(0.99 0 0);
  --hd-secondary: oklch(0.97 0.005 260);
  --hd-secondary-foreground: oklch(0.22 0.02 260);
  --hd-muted: oklch(0.97 0.005 260);
  --hd-muted-foreground: oklch(0.5 0.015 258);
  --hd-accent: oklch(0.97 0.01 220);
  --hd-accent-foreground: oklch(0.22 0.02 260);

  --hd-destructive: oklch(0.62 0.21 25);
  --hd-destructive-foreground: oklch(0.99 0 0);

  --hd-border: oklch(0.93 0.006 260);
  --hd-input: oklch(0.93 0.006 260);
  --hd-ring: oklch(0.7 0.12 220);

  /* Brand gradient. v2 replaced the blue→cyan ramp with a magenta→violet→blue
     one and, unlike the old pair, uses the identical stops in both themes. */
  --hd-houdiny-from: #cb348c;
  --hd-houdiny-to: #2580f3;

  /* Loading affordances. Skeletons shimmer between the two stops; spinners and
     dot loaders take their ink from --hd-loader-*; --hd-overlay is the scrim a
     blocking loader lays over the content it covers. Light values reproduce the
     pre-theme appearance exactly, so nothing shifts in light mode. */
  --hd-skeleton-base: #ececec;
  --hd-skeleton-highlight: #fafafa;
  --hd-loader-dot: #6d91b9;
  --hd-loader-ring: #1b1f3c;
  --hd-overlay: rgba(255, 255, 255, 0.5);

  /* Custom (non-native) scrollbars on the thread and message panes. */
  --hd-scrollbar-thumb: #cbd5e1;

  /* Inbox status chips. The reference hardcodes Tailwind's *-50/-200/-700
     pastels, which have no dark variant there; these reproduce them exactly in
     light and get a tinted-on-dark counterpart below. */
  --hd-status-interested-bg: #ecfdf5;
  --hd-status-interested-fg: #047857;
  --hd-status-interested-border: #a7f3d0;

  --hd-status-meeting-bg: #f5f3ff;
  --hd-status-meeting-fg: #6d28d9;
  --hd-status-meeting-border: #ddd6fe;

  --hd-status-snoozed-bg: #fffbeb;
  --hd-status-snoozed-fg: #b45309;
  --hd-status-snoozed-border: #fde68a;

  --hd-status-declined-bg: #fff1f2;
  --hd-status-declined-fg: #be123c;
  --hd-status-declined-border: #fecdd3;

  /* LinkedIn brand blue — legible on white, too dark on near-black. */
  --hd-linkedin: #0a66c2;

  /* Accent text on a 10%-tint pill (account status, warm-up chips). The
     reference writes these as text-{hue}-600 dark:text-{hue}-400. */
  --hd-accent-emerald: #059669;
  --hd-accent-amber: #d97706;
  --hd-accent-rose: #e11d48;

  --hd-gradient-houdiny: #000;
  --hd-gradient-text: var(--hd-gradient-houdiny);
  --hd-gradient-soft: linear-gradient(135deg, rgba(37, 99, 235, 0.08) 0%, rgba(6, 182, 212, 0.08) 100%);
  --hd-shadow-card: 0 1px 2px rgba(15, 23, 42, 0.04), 0 8px 24px -12px rgba(15, 23, 42, 0.08);
  --hd-shadow-card-hover: 0 1px 2px rgba(15, 23, 42, 0.05), 0 24px 48px -20px rgba(37, 99, 235, 0.18);
  --hd-shadow-elevated: 0 1px 2px rgba(15, 23, 42, 0.05), 0 30px 60px -24px rgba(15, 23, 42, 0.18);
`;

/** Dark palette. Applied via `[data-theme='dark']` / `html.dark`. */
export const darkTokens = css`
  --hd-background: #121212;
  --hd-foreground: #f2f2f2;

  --hd-card: #1a1a1a;
  --hd-card-foreground: #f2f2f2;
  --hd-popover: #1e1e1e;
  --hd-popover-foreground: #f2f2f2;

  --hd-primary: oklch(0.7 0.18 255);
  --hd-primary-foreground: oklch(0.12 0.02 260);
  --hd-secondary: #242424;
  --hd-secondary-foreground: #f2f2f2;
  --hd-muted: #232323;
  --hd-muted-foreground: #a3a3a3;
  --hd-accent: oklch(0.26 0.03 250);
  --hd-accent-foreground: #f2f2f2;

  --hd-destructive: oklch(0.65 0.22 25);
  --hd-destructive-foreground: oklch(0.99 0 0);

  --hd-border: #2e2e2e;
  --hd-input: #2e2e2e;
  --hd-ring: oklch(0.6 0.15 230);

  /* Same stops as light — v2 stopped lightening the brand ramp on dark. */
  --hd-houdiny-from: #cb348c;
  --hd-houdiny-to: #2580f3;

  /* Skeletons sit just above --hd-card (0.2) so a placeholder reads as a raised
     block rather than a hole, and stay below --hd-border (0.3) so they never
     shout. The loader inks lighten to stay visible on a near-black surface —
     the light-mode navy ring would all but disappear here. */
  --hd-skeleton-base: #242424;
  --hd-skeleton-highlight: #2e2e2e;
  --hd-loader-dot: oklch(0.7 0.18 255);
  --hd-loader-ring: oklch(0.75 0.15 255);
  --hd-overlay: rgba(0, 0, 0, 0.6);

  --hd-scrollbar-thumb: #3a3a3a;

  /* Status chips: a low-opacity wash of the hue instead of the light pastel,
     with the 300-level text that pastel schemes pair with on dark. */
  --hd-status-interested-bg: color-mix(in srgb, #10b981 14%, transparent);
  --hd-status-interested-fg: #6ee7b7;
  --hd-status-interested-border: color-mix(in srgb, #10b981 32%, transparent);

  --hd-status-meeting-bg: color-mix(in srgb, #8b5cf6 14%, transparent);
  --hd-status-meeting-fg: #c4b5fd;
  --hd-status-meeting-border: color-mix(in srgb, #8b5cf6 32%, transparent);

  --hd-status-snoozed-bg: color-mix(in srgb, #f59e0b 14%, transparent);
  --hd-status-snoozed-fg: #fcd34d;
  --hd-status-snoozed-border: color-mix(in srgb, #f59e0b 32%, transparent);

  --hd-status-declined-bg: color-mix(in srgb, #f43f5e 14%, transparent);
  --hd-status-declined-fg: #fda4af;
  --hd-status-declined-border: color-mix(in srgb, #f43f5e 32%, transparent);

  --hd-linkedin: #70b5f9;

  --hd-accent-emerald: #34d399;
  --hd-accent-amber: #fbbf24;
  --hd-accent-rose: #fb7185;

  /* Dark-only stand-ins for hard-coded slate hexes in older styles, written as
     var(--legacy-fg, #1e293b): unset in light, so the hex still applies there. */
  --legacy-fg: var(--hd-foreground);
  --legacy-fg-2: #d4d4d4;
  --legacy-muted: var(--hd-muted-foreground);
  --legacy-border: var(--hd-border);
  --legacy-fill: var(--hd-muted);

  --hd-gradient-houdiny: #000;
  --hd-gradient-text: linear-gradient(var(--hd-foreground), var(--hd-foreground));
  --hd-gradient-soft: linear-gradient(135deg, rgba(96, 165, 250, 0.12) 0%, rgba(34, 211, 238, 0.12) 100%);
  --hd-shadow-card: 0 1px 2px rgba(0, 0, 0, 0.3), 0 8px 24px -12px rgba(0, 0, 0, 0.5);
  --hd-shadow-card-hover: 0 1px 2px rgba(0, 0, 0, 0.4), 0 24px 48px -20px rgba(96, 165, 250, 0.35);
  --hd-shadow-elevated: 0 1px 2px rgba(0, 0, 0, 0.4), 0 30px 60px -24px rgba(0, 0, 0, 0.6);
`;

/**
 * Re-points the app's pre-existing *semantic* globals at the dark palette so
 * legacy screens stay legible without being rewritten.
 *
 * Only unambiguous tokens are remapped. `--white`, `--black` and `--primary`
 * are deliberately left alone: they are used both as surfaces and as
 * text-on-colour, so flipping them would invert text on gradient buttons.
 */
export const darkLegacyOverrides = css`
  --body-bg: var(--hd-background);
  --base-background-color: var(--hd-background);
  --base-text-color: var(--hd-muted-foreground);
  --text-color: var(--hd-muted-foreground);
  --text-color-50: var(--hd-foreground);
  --headings-color: var(--hd-foreground);
  --link-color: var(--hd-primary);

  --form-element-border-color: var(--hd-border);
  --form-element-focus-border-color: var(--hd-ring);
  --placeholder-color: var(--hd-muted-foreground);
  --table-border: var(--hd-border);

  --gray: var(--hd-muted);
  --gray-100: var(--hd-muted);
  --gray-200: var(--hd-muted);
  --gray-250: var(--hd-border);
  --gray-350: var(--hd-muted);
  --gray-400: var(--hd-border);
  --gray-450: var(--hd-border);
  --light: var(--hd-muted);
`;
